import React from 'react';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';

function Receipt({
  cart = [],
  subtotal = 0,
  vat = 0,
  shipping = 0,
  total = 0,
  discount = 0,
  wht = 0,
  paid = null,
  formatPrice,
  business = {},
  shippingAddress = {},
  customer = {},
  receiptNumber = null,      // ← NEW: formatted from backend (RV00000067)
  transactionRef = null,     // fallback
}) {
  const { primaryColor = '#556B2F' } = useTheme();

  const safeFormatPrice = (value) => {
    if (typeof formatPrice === 'function') return formatPrice(value);
    return `N ${Number(value || 0).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const money = (num) => {
    return Number(Math.abs(num || 0)).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const escape = (str) =>
    str
      ? String(str).replace(
          /[&<>"']/g,
          (c) =>
            ({
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            })[c]
        )
      : '';

  const generateFallbackReceiptNumber = () => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `REC-${date}-${random}`;
  };

  // Priority: formatted backend receipt → Paystack ref → random
  const finalReceiptNo =
    receiptNumber ||
    (transactionRef
      ? `PAY-${transactionRef}`
      : generateFallbackReceiptNumber());

  const generateReceipt = () => {
    if (!cart?.length) {
      alert('No items in cart to generate receipt');
      return;
    }

    const today = new Date().toLocaleDateString('en-GB');

    const customerName =
      shippingAddress?.name ||
      customer?.full_name ||
      customer?.business_name ||
      'Walk-in Customer';

    const customerContact =
      [
        shippingAddress?.phone || customer?.phone || '',
        shippingAddress?.email || customer?.email || '',
      ]
        .filter(Boolean)
        .join(' | ') || '—';

    const discountAmt = (discount / 100) * subtotal;
    const vatAmt = (vat / 100) * subtotal;
    const whtAmt = (wht / 100) * subtotal;
    const netPayable = total - whtAmt;
    const actualPaid = paid ?? total;
    const balance = total - actualPaid;

    const itemRows = cart
      .map((item, i) => {
        let desc = escape(item.name || 'Item');

        if (item.selectedSize) {
          desc += ` • Size: ${escape(item.selectedSize)}`;
        }

        if (item.selectedColor) {
          desc += ` • Color: ${escape(item.selectedColor)}`;
        }

        if (item.format) {
          desc += ` • Format: ${escape(item.format)}`;
        }

        return `
          <tr>
            <td style="padding:10px 8px; border-bottom:1px solid #666;">
              ${i + 1}
            </td>

            <td style="padding:10px 8px; border-bottom:1px solid #666;">
              ${desc}
            </td>

            <td style="padding:10px 8px; border-bottom:1px solid #666; text-align:center;">
              ${item.quantity || item.qty || 1}
            </td>

            <td
              style="padding:10px 8px; border-bottom:1px solid #666; text-align:right; font-family:'Courier New',monospace;"
              class="amount"
            >
              ${money(item.price)}
            </td>

            <td
              style="padding:10px 8px; border-bottom:1px solid #666; text-align:right; font-family:'Courier New',monospace;"
              class="amount"
            >
              ${money(
                item.price * (item.quantity || item.qty || 1)
              )}
            </td>
          </tr>
        `;
      })
      .join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Receipt - ${finalReceiptNo}</title>

        <style>
          @page {
            size: A4 portrait;
            margin: 10mm 12mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 0;
            font-family: 'Georgia', serif;
            font-size: 10pt;
            line-height: 1.45;
            color: #000;
          }

          .container {
            width: calc(100% - 10mm);
            max-width: calc(100% - 10mm);
            margin: 0 auto;
            padding: 0;
          }

          .letterhead {
            border-bottom: 3px double #000;
            padding: 18px 0 22px;
            margin-bottom: 22px;
          }

          .header-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 86px;
          }

          .logo {
            height: 86px;
            width: auto;
            object-fit: contain;
          }

          .company-name {
            flex: 1;
            text-align: center;
            font-size: 18pt;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .registration {
            font-size: 10.5pt;
            font-style: italic;
            color: #333;
            white-space: nowrap;
            padding-left: 20px;
          }

          .tagline {
            text-align: center;
            font-style: italic;
            font-size: 12pt;
            margin: 4px 0 0;
            color: #444;
          }

          .title {
            text-align: center;
            font-size: 26pt;
            font-weight: bold;
            margin: 30px 0 20px;
            letter-spacing: 3px;
            text-transform: uppercase;
            border-bottom: 2.5px double #000;
            padding-bottom: 10px;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 25px 0;
            font-size: 11pt;
          }

          .info-row > div {
            min-width: 0;
            max-width: 48%;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          table {
            width: 100%;
            max-width: 100%;
            table-layout: fixed;
            border-collapse: collapse;
            margin: 25px 0;
            font-size: 11pt;
          }

          th {
            background: #000;
            color: white;
            padding: 10px 8px;
            text-align: left;
            font-weight: bold;
          }

          td {
            padding: 10px 8px;
            border-bottom: 1px solid #666;
          }

          .text-right {
            text-align: right;
          }

          .amount {
            font-family: 'Courier New', monospace;
            font-weight: bold;
            white-space: nowrap;
          }

          .summary {
            width: 50%;
            margin-left: auto;
            font-size: 12pt;
            border-top: 2px solid #000;
            margin-top: 30px;
          }

          .summary td {
            border: none;
            padding: 7px 0;
          }

          .total-row {
            background: #e8e8e8;
            font-weight: bold;
            font-size: 13pt;
          }

          .highlight-row {
            background: #fff3cd;
            color: #856404;
          }

          .net-payable {
            background: #d4edda;
            font-size: 14pt;
          }

          .balance-due {
            background: #ffebee;
          }

          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 9pt;
            color: #555;
            font-style: italic;
          }
        </style>
      </head>

      <body>
        <div class="container">
          <div class="letterhead">
            <div class="header-row">
              <div>
                ${
                  business?.logo
                    ? `<img src="${business.logo}" class="logo" alt="Logo">`
                    : '<div style="width:90px;height:86px;"></div>'
                }
              </div>

              <div class="company-name">
                ${escape(business?.name || 'COMPANY LIMITED')}
              </div>

              <div class="registration">
                ${escape(business?.registration || '')}
              </div>
            </div>

            ${
              business?.tagline
                ? `<div class="tagline">${escape(
                    business.tagline
                  )}</div>`
                : ''
            }
          </div>

          <h1 class="title">Receipt</h1>

          <div class="info-row">
            <div>
              <strong>Receipt No:</strong> ${finalReceiptNo}<br>
              <strong>Date:</strong> ${today}
            </div>

            <div style="text-align:right;">
              <strong>Received from:</strong><br>
              ${escape(customerName)}<br>
              ${escape(customerContact)}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:5%">#</th>
                <th>Description</th>
                <th style="width:10%">Qty</th>
                <th style="width:18%" class="text-right">
                  Unit Price
                </th>
                <th style="width:20%" class="text-right">
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              ${itemRows}
            </tbody>
          </table>

          <table class="summary">
            <tbody>
              <tr>
                <td>Subtotal</td>
                <td class="text-right amount">
                  ${money(subtotal)}
                </td>
              </tr>

              ${
                discount > 0
                  ? `
                    <tr>
                      <td>Discount (${discount}%)</td>
                      <td class="text-right amount">
                        -${money(discountAmt)}
                      </td>
                    </tr>
                  `
                  : ''
              }

              <tr>
                <td>VAT (${vat || 0}%)</td>
                <td class="text-right amount">
                  ${money(vatAmt)}
                </td>
              </tr>

              <tr>
                <td>Logistics / Shipping</td>
                <td class="text-right amount">
                  ${money(shipping)}
                </td>
              </tr>

              ${
                wht > 0
                  ? `
                    <tr class="highlight-row">
                      <td>
                        <strong>Less: WHT (${wht}%)</strong>
                      </td>
                      <td class="text-right amount">
                        <strong>-${money(whtAmt)}</strong>
                      </td>
                    </tr>
                  `
                  : ''
              }

              <tr class="total-row">
                <td>
                  <strong>TOTAL AMOUNT</strong>
                </td>
                <td class="text-right amount">
                  <strong>${money(total)}</strong>
                </td>
              </tr>

              ${
                wht > 0
                  ? `
                    <tr class="net-payable">
                      <td>
                        <strong>NET PAYABLE</strong>
                      </td>
                      <td
                        class="text-right amount"
                        style="color:#155724;"
                      >
                        <strong>${money(netPayable)}</strong>
                      </td>
                    </tr>
                  `
                  : ''
              }

              <tr>
                <td>Amount Paid</td>
                <td class="text-right amount">
                  ${money(actualPaid)}
                </td>
              </tr>

              ${
                balance > 0
                  ? `
                    <tr class="balance-due">
                      <td>
                        <strong>Balance Due</strong>
                      </td>
                      <td
                        class="text-right amount"
                        style="color:red"
                      >
                        <strong>${money(balance)}</strong>
                      </td>
                    </tr>
                  `
                  : ''
              }
            </tbody>
          </table>

          <div class="footer">
            Thank you for your patronage! •
            ${escape(business?.name || 'Brand Name')} •
            Printed on: ${today}
          </div>
        </div>
      </body>
      </html>
    `;

    if (window.html2pdf) {
      window
        .html2pdf()
        .set({
          margin: [10, 12, 20, 12],
          filename: `receipt_${finalReceiptNo.replace(
            /[^a-zA-Z0-9-]/g,
            '_'
          )}.pdf`,
          image: {
            type: 'jpeg',
            quality: 0.98,
          },
          html2canvas: {
            scale: 2.2,
            useCORS: true,
            logging: false,
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait',
          },
          pagebreak: {
            mode: ['avoid-all', 'css', 'legacy'],
          },
        })
        .from(htmlContent)
        .save();
    } else {
      alert(
        'html2pdf library not loaded. Please include the script in index.html'
      );
    }
  };

  return (
    <Button
      onClick={generateReceipt}
      className="w-full bg-primary text-white px-6 py-3 text-lg font-montserrat rounded-full hover:bg-primary/90 mt-6"
      style={{ '--primary': primaryColor }}
      disabled={!cart?.length}
    >
      Download Receipt (PDF)
    </Button>
  );
}

export default Receipt;